import React, { useState, useEffect } from "react";
import { persistor } from "../store";
import { Route, Switch, withRouter } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import withStyles from "@material-ui/core/styles/withStyles";
import * as api from "../core-module/services/api";

import Home from "../views/home";
import Inside from "../views/inside";
import Outside from "../views/outside";
import AutoLocation from "../views/autolocation";
import Form from "../views/form";
import ShortHome from "../views/s/home";
import ShortInside from "../views/s/inside";
import ShortOutside from "../views/s/outside";
import ShortAutoLocation from "../views/s/autolocation";
import ShortForm from "../views/s/form";
import ShortFinResidential from "../views/s/fin/residential";
import ShortFinBusiness from "../views/s/fin/business";
import ShortFinGigAir from "../views/s/fin/gigair";
import InstallAvailability from "../views/s/installAvailability";
import FinResidential from "../views/fin/residential";
import FinBusiness from "../views/fin/business";
import FinGigAir from "../views/fin/gigair";

import config from "../config";
import getVar from "../core-module/tiny-functions/getVar";
import { clearMetaEventId } from "../metaConversions";

import logo from "../assets/img/mcsnetLogo.png";

import heroImg from "../assets/img/FiberFlow.jpeg";
import pageImg from "../assets/img/FadedBg.webp";
import colors from "../assets/colors";

const SIGNUP_EVENT_CODE_PARAM = "signupEventCode";
const SIGNUP_EVENT_FLAG_PATTERN = /^[A-Z0-9_-]+$/;

const getSignupEventCodeFromSearch = search => {
  const params = new URLSearchParams(search || "");
  const signupEventCode = params.get(SIGNUP_EVENT_CODE_PARAM);
  return signupEventCode ? signupEventCode.trim() : "";
};

const isShortSignupPath = pathname => {
  const shortSignupPath = config.SIGNUP.APP_PATH + "/s";
  return (
    pathname === shortSignupPath || pathname.startsWith(shortSignupPath + "/")
  );
};

const setSignupEventCodeOnPath = (path, signupEventCode) => {
  const [pathname, search = ""] = path.split("?");
  const params = new URLSearchParams(search);
  params.set(SIGNUP_EVENT_CODE_PARAM, signupEventCode);
  return `${pathname}?${params.toString()}`;
};

const styles = theme => ({
  page: { position: "relative", minHeight: "100vh", backgroundColor: "#fff" },

  pageBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 680,
    pointerEvents: "none",
    backgroundImage: `
      linear-gradient(
        to bottom,
        rgba(255,255,255,0) 74%,
        rgba(255,255,255,0.65) 93%,
        #ffffff 100%
      ),
      url(${pageImg})
    `,
    backgroundSize: "100% 100%, cover",
    backgroundRepeat: "no-repeat, no-repeat",
    backgroundPosition: "center top, center 150px",
    zIndex: 0
  },

  shell: {
    maxWidth: 1280,
    width: "100%",
    margin: "0 auto",
    padding: theme.spacing(0, 2),
    position: "relative",
    zIndex: 1
  },

  headerContainer: {
    paddingLeft: 5,
    paddingRight: 5,
    marginBottom: "1.5rem",
    alignItems: "center"
  },

  heroBox: {
    width: "clamp(280px, 76vw, 760px)",
    margin: "12px auto 18px",
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 10px 30px rgba(0,0,0,.12)"
  },

  heroViewport: {
    position: "relative",
    height: 230,
    [theme.breakpoints.down("sm")]: {
      height: "auto"
    }
  },

  heroImgNative: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "auto",
    display: "block",
    imageRendering: "auto",
    [theme.breakpoints.down("sm")]: {
      position: "relative",
      bottom: "auto",
      left: "auto"
    }
  },

  // add a gentle bottom gradient so white text pops
  heroShade: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "55%",
    background:
      "linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.0) 70%)",
    pointerEvents: "none"
  },

  // bottom-center title over the image
  heroTitle: {
    position: "absolute",
    bottom: 16,
    left: "50%",
    transform: "translateX(-50%)",
    margin: 0,
    color: "#fff",
    fontWeight: 700,
    paddingBottom: theme.spacing(2),
    fontSize: 28,
    letterSpacing: ".2px",
    textShadow: "0 2px 6px rgba(0,0,0,0.35)",
    [theme.breakpoints.down("sm")]: { fontSize: 22, bottom: 12 }
  },

  routeContainer: { marginBottom: theme.spacing(6) },
  appLogo: {
    height: 96, // was maxWidth: 100 — a touch bigger
    width: "auto",
    padding: "12px 20px 24px 20px",
    display: "block",
    [theme.breakpoints.down("sm")]: {
      height: 96 // slightly smaller on phones
    }
  },

  startOverButton: {
    height: 40,
    backgroundColor: colors.mcsred,
    color: "#fff",
    borderRadius: 10,
    textTransform: "none",
    "&:hover": { backgroundColor: colors.mcsred } // keep exact brand red on hover
  },

  signupEventBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    margin: "0 5px 18px",
    padding: theme.spacing(1.25, 2),
    border: "1px solid rgba(221, 52, 73, 0.28)",
    borderRadius: 8,
    backgroundColor: "#fff8e8",
    color: "#7a3d00",
    boxShadow: "0 6px 16px rgba(9, 30, 66, 0.08)",
    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
      flexDirection: "column"
    }
  },

  signupEventBannerTitle: {
    margin: 0,
    color: colors.mcsdark,
    fontWeight: 800,
    fontSize: 15,
    fontFamily: '"Ubuntu","Helvetica","Arial",sans-serif'
  },

  signupEventBannerText: {
    margin: "2px 0 0",
    color: "#7a3d00",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: '"Ubuntu","Helvetica","Arial",sans-serif'
  },

  signupEventErrorBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    margin: "0 5px 18px",
    padding: theme.spacing(1.25, 2),
    border: "1px solid rgba(221, 52, 73, 0.34)",
    borderRadius: 8,
    backgroundColor: "#fff3f4",
    color: "#8f1d2f",
    boxShadow: "0 6px 16px rgba(9, 30, 66, 0.08)"
  },

  signupEventErrorText: {
    margin: 0,
    color: "#8f1d2f",
    fontSize: 15,
    fontWeight: 700,
    fontFamily: '"Ubuntu","Helvetica","Arial",sans-serif'
  }
});

const Main = props => {
  const [signupclass, setSignupclass] = useState("");
  const [gigair, setGigair] = useState(false);
  const [areaid, setAreaid] = useState(null);
  const [promoCode, setPromoCode] = useState(null);
  const [areaName, setAreaName] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [contactid, setContactid] = useState("");
  const [signupreflink, setSignupreflink] = useState("");
  const [regularpackages, setRegularPackages] = useState([]);
  const [promocatalog, setPromoCatalog] = useState(null);
  const [signupEvent, setSignupEvent] = useState(null);
  const [signupEventValidation, setSignupEventValidation] = useState("idle");
  const [expiredSignupEventCode, setExpiredSignupEventCode] = useState("");

  const currentPathIsShortSignup = isShortSignupPath(props.location.pathname);
  const signupEventCode = signupEvent ? signupEvent.signupEventCode : "";
  const activeSignupEvent =
    !currentPathIsShortSignup &&
    signupEventCode !== "" &&
    signupEventValidation === "valid";

  useEffect(() => {
    if (props.agent != null) {
      setSignupclass("");
      setAreaid(null);
      setAreaName("");
      setLat(null);
      setLng(null);
      setContactid("");
      setSignupreflink("");
      setRegularPackages([]);
      setPromoCatalog(null);

      localStorage.removeItem("persist:root");
      sessionStorage.removeItem("signupglobals");
      sessionStorage.removeItem("signupformPersistentState");
      sessionStorage.removeItem("shortSignupLocationMethod");
      clearMetaEventId();
    } else {
      let signupglobals = window.sessionStorage.getItem("signupglobals");
      if (signupglobals != null) {
        signupglobals = JSON.parse(signupglobals);
        setSignupclass(signupglobals.signupclass);
        setAreaid(signupglobals.areaid);
        setAreaName(signupglobals.areaName);
        setLat(signupglobals.lat);
        setLng(signupglobals.lng);
      }
    }
    if (
      props.location.pathname === "/gigair" ||
      props.location.pathname === "/s/gigair"
    ) {
      setGigair(true);
    }

    const promocodegetvar = getVar("promocode");
    if (promocodegetvar) {
      setPromoCode(promocodegetvar);
    }

    const initialSignupEventCode = getSignupEventCodeFromSearch(
      props.history.location.search
    );

    if (initialSignupEventCode) {
      props.history.replace(
        setSignupEventCodeOnPath(
          props.history.location.pathname,
          initialSignupEventCode
        )
      );
    } else {
      props.history.push(props.history.location.pathname);
    }
  }, []);

  useEffect(() => {
    const requestedSignupEventCode = getSignupEventCodeFromSearch(
      props.location.search
    );

    if (!requestedSignupEventCode || currentPathIsShortSignup) {
      setExpiredSignupEventCode("");
      return;
    }

    if (signupEventCode === requestedSignupEventCode) {
      return;
    }

    let cancelled = false;
    setSignupEventValidation("loading");

    api
      .get("guest.1/signupevent", {
        schema: 0,
        httpGetParameters: {
          signupEventCode: requestedSignupEventCode
        }
      })
      .then(res => {
        if (cancelled) {
          return;
        }

        if (
          res &&
          res.object &&
          res.object.signupEventCode &&
          res.object.flag &&
          SIGNUP_EVENT_FLAG_PATTERN.test(res.object.flag)
        ) {
          setSignupEvent({
            signupEventCode: String(res.object.signupEventCode),
            flag: String(res.object.flag)
          });
          setExpiredSignupEventCode("");
          setSignupEventValidation("valid");
          return;
        }

        setSignupEvent(null);
        setExpiredSignupEventCode(requestedSignupEventCode);
        setSignupEventValidation("invalid");
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setSignupEvent(null);
        setExpiredSignupEventCode(requestedSignupEventCode);
        setSignupEventValidation("invalid");
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentPathIsShortSignup,
    props.history,
    props.location.pathname,
    props.location.search,
    signupEventCode
  ]);

  useEffect(() => {
    window.sessionStorage.setItem(
      "signupglobals",
      JSON.stringify({
        signupclass,
        areaid,
        areaName,
        lat,
        lng
      })
    );
  }, [signupclass, areaid, areaName, lat, lng]);

  const routeTitle = () => {
    const p = props.location.pathname;
    if (p.includes("/s/fin-")) return "";
    if (p.includes("/inside")) return "Your Location";
    if (p.includes("/outside")) return "Check Availability";
    if (p.includes("/autolocation")) return "Find Your Location";
    if (p.includes("/form")) return "Check Availability";
    if (p.includes("/fin-residential")) return "Sign-Up Complete";
    if (p.includes("/fin-business")) return "Sign-Up Complete";
    if (p.includes("/fin-gigair")) return "Sign-Up Complete";
    return "Check Availability";
  };

  const { classes } = props;

  const startOver = () => {
    const startPath = props.location.pathname.includes("/s")
      ? config.SIGNUP.APP_PATH + "/s/"
      : activeSignupEvent
      ? setSignupEventCodeOnPath(config.SIGNUP.APP_PATH + "/", signupEventCode)
      : config.SIGNUP.APP_PATH + "/";

    persistor.pause();
    persistor.flush().then(() =>
      persistor.purge().then(() => {
        setSignupclass("");
        setAreaid(null);
        setAreaName("");
        setLat(null);
        setLng(null);
        setContactid("");
        setSignupreflink("");
        setRegularPackages([]);
        setPromoCatalog(null);
        localStorage.removeItem("persist:root");
        sessionStorage.removeItem("signupglobals");
        sessionStorage.removeItem("signupformPersistentState");
        sessionStorage.removeItem("shortSignupLocationMethod");
        clearMetaEventId();
        props.history.push(startPath);
      })
    );
  };

  return (
    <div className={classes.page}>
      <div className={classes.pageBackdrop} /> {/* NEW */}
      <div className={classes.shell}>
        <Grid
          container
          className={classes.headerContainer}
          justifyContent="space-between"
        >
          <img src={logo} className={classes.appLogo} alt="logo" />
          <Button
            onClick={startOver}
            className={classes.startOverButton}
            variant="contained"
          >
            <h4>START OVER</h4>
          </Button>
        </Grid>

        {activeSignupEvent ? (
          <div className={classes.signupEventBanner} role="status">
            <div>
              <p className={classes.signupEventBannerTitle}>
                Signup event mode is active
              </p>
              <p className={classes.signupEventBannerText}>
                Event code: {signupEventCode}
              </p>
            </div>
          </div>
        ) : null}

        {signupEventValidation === "invalid" && expiredSignupEventCode ? (
          <div className={classes.signupEventErrorBanner} role="alert">
            <p className={classes.signupEventErrorText}>
              This signup event code has expired.
            </p>
          </div>
        ) : null}

        <div className={classes.heroBox}>
          <div className={classes.heroViewport}>
            <img
              src={heroImg}
              alt=""
              className={classes.heroImgNative}
              loading="eager"
              decoding="sync"
            />
            <div className={classes.heroShade} />
            {routeTitle() ? (
              <h1 className={classes.heroTitle}>{routeTitle()}</h1>
            ) : null}
          </div>
        </div>

        <Grid
          container
          justifyContent="center"
          className={classes.routeContainer}
        >
          <Switch>
            <Route exact path={config.SIGNUP.APP_PATH + "/s/"}>
              <ShortHome setSignupclass={setSignupclass} />
            </Route>
            <Route exact path={config.SIGNUP.APP_PATH + "/s/gigair"}>
              <ShortHome setSignupclass={setSignupclass} />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/s/inside"}>
              <ShortInside
                setAreaid={setAreaid}
                setAreaName={setAreaName}
                setSignupclass={setSignupclass}
              />
            </Route>
            <Route
              path={config.SIGNUP.APP_PATH + "/s/outside"}
              component={ShortOutside}
            />
            <Route path={config.SIGNUP.APP_PATH + "/s/autolocation"}>
              <ShortAutoLocation setLat={setLat} setLng={setLng} />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/s/form"}>
              <ShortForm
                autoFillCustomValue={{
                  signupclass,
                  areaid,
                  lat,
                  lng,
                  agent: Number(props.agent),
                  gigair,
                  promoCode
                }}
                extraPostValues={
                  signupclass === "inside" && areaid != null ? { areaid } : null
                }
                agent={Number(props.agent)}
                needCaptcha={props.needCaptcha}
                areaId={areaid}
                areaName={areaName}
                setLat={setLat}
                setLng={setLng}
                setAreaid={setAreaid}
                setAreaName={setAreaName}
                setContactid={setContactid}
                setSignupreflink={setSignupreflink}
                setPromoCatalog={setPromoCatalog}
                startOver={startOver}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/s/fin-residential"}>
              <ShortFinResidential
                type="residential"
                agent={props.agent}
                contactid={contactid}
                signupreflink={signupreflink}
                promocatalog={promocatalog}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/s/fin-gigair"}>
              <ShortFinGigAir
                type="gigair"
                agent={props.agent}
                contactid={contactid}
                signupreflink={signupreflink}
                promocatalog={promocatalog}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/s/fin-business"}>
              <ShortFinBusiness
                type="business"
                agent={props.agent}
                contactid={contactid}
                signupreflink={signupreflink}
                promocatalog={promocatalog}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/s/install-availability"}>
              <InstallAvailability />
            </Route>
            <Route exact path={config.SIGNUP.APP_PATH + "/"}>
              <Home
                setSignupclass={setSignupclass}
                signupEventCode={activeSignupEvent ? signupEventCode : ""}
              />
            </Route>
            <Route exact path={config.SIGNUP.APP_PATH + "/gigair"}>
              <Home
                setSignupclass={setSignupclass}
                signupEventCode={activeSignupEvent ? signupEventCode : ""}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/inside"}>
              <Inside
                setAreaid={setAreaid}
                setAreaName={setAreaName}
                setSignupclass={setSignupclass}
                signupEventCode={activeSignupEvent ? signupEventCode : ""}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/outside"}>
              <Outside
                signupEventCode={activeSignupEvent ? signupEventCode : ""}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/autolocation"}>
              <AutoLocation
                setLat={setLat}
                setLng={setLng}
                signupEventCode={activeSignupEvent ? signupEventCode : ""}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/form"}>
              <Form
                autoFillCustomValue={{
                  signupclass,
                  areaid,
                  lat,
                  lng,
                  agent: Number(props.agent),
                  gigair,
                  promoCode
                }}
                extraPostValues={
                  signupclass === "inside" && areaid != null ? { areaid } : null
                }
                agent={Number(props.agent)}
                needCaptcha={props.needCaptcha}
                areaId={areaid}
                areaName={areaName}
                setContactid={setContactid}
                setRegularPackages={setRegularPackages}
                signupEventCode={
                  activeSignupEvent ? signupEvent.signupEventCode : ""
                }
                startOver={startOver}
              />{" "}
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/fin-residential"}>
              <FinResidential
                type="residential"
                agent={props.agent}
                contactid={contactid}
                regularpackages={regularpackages}
                signupEventCode={
                  activeSignupEvent ? signupEvent.signupEventCode : ""
                }
                startOver={startOver}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/fin-gigair"}>
              <FinGigAir
                type="gigair"
                agent={props.agent}
                contactid={contactid}
                regularpackages={regularpackages}
                signupEventCode={
                  activeSignupEvent ? signupEvent.signupEventCode : ""
                }
                startOver={startOver}
              />
            </Route>
            <Route path={config.SIGNUP.APP_PATH + "/fin-business"}>
              <FinBusiness
                type="business"
                agent={props.agent}
                contactid={contactid}
                regularpackages={regularpackages}
                signupEventCode={
                  activeSignupEvent ? signupEvent.signupEventCode : ""
                }
                startOver={startOver}
              />
            </Route>
          </Switch>
        </Grid>
      </div>
    </div>
  );
};

export default withStyles(styles)(withRouter(Main));
